import { LiftIgniterChecker, logWarn, logInfo } from './liftIgniterChecker';

const BATCH_MESSAGE_FREQUENCY = 1000;
const MAX_BATCH_SIZE_PER_EVENT_NAME = 32;

type CreateSendOptions<T extends string, B> = (batch: Partial<Record<T, B[]>>) => any;

export class LiftIgniterBatcher<TEventName extends string, TBatch = any> {
    private timeout: ReturnType<typeof setTimeout> | null = null;
    private batch: Partial<Record<TEventName, TBatch[]>> = {};

    constructor(
        private batchName: string,
        private createSendOptions?: CreateSendOptions<TEventName, TBatch>,
        private checker = new LiftIgniterChecker()
    ) {}

    track(eventName: TEventName, sendEvent: any) {
        if (this.timeout === null) {
            this.timeout = setTimeout(() => this.sendBatch(), BATCH_MESSAGE_FREQUENCY);
            logInfo(`$p: added ${eventName} to new batch`);
        } else {
            logInfo(`$p: added ${eventName} to existing batch`);
        }

        this.batch[eventName] ||= [];
        this.batch[eventName]!.push(sendEvent);
    }

    private sendBatch() {
        this.timeout = null;

        (Object.keys(this.batch) as TEventName[]).forEach((eventName: TEventName) => {
            const batch = this.batch[eventName]!;
            const countToDiscard = batch.length - MAX_BATCH_SIZE_PER_EVENT_NAME;
            if (countToDiscard > 0) {
                logWarn(`$p: Discarding ${countToDiscard} old items`);
                batch.splice(0, countToDiscard);
            }
        });

        if (this.checker.isReady()) {
            const countsString = (Object.keys(this.batch) as TEventName[])
                .map((eventName) => `${this.batch[eventName]!.length} ${eventName}s`)
                .join(', ');

            logInfo(`$p: Sending ${countsString}`);

            const sendOptions = {
                ...this.makeBatch(this.batch),
                ...(this.createSendOptions?.(this.batch) || {}),
            };

            this.batch = {};
            this.checker.send(this.batchName, sendOptions);
        } else {
            this.timeout = setTimeout(() => this.sendBatch(), BATCH_MESSAGE_FREQUENCY);
            logInfo(
                `$p: LiftIgniter not loaded during run of _sendBatch;` +
                    `will retry after ${BATCH_MESSAGE_FREQUENCY}ms`
            );
        }
    }

    private makeBatch(batch: Partial<Record<TEventName, TBatch[]>>): any {
        return (Object.keys(batch) as TEventName[]).reduce((result, eventName) => {
            result[eventName] = (batch[eventName] || []).map((x) => JSON.stringify(x));
            return result;
        }, {} as Record<TEventName, any>);
    }
}
